import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

type NotificationVariant = "success" | "error" | "info";

type NotificationItem = {
  id: number;
  title?: string;
  message: string;
  variant: NotificationVariant;
  duration?: number;
};

type NotificationContextValue = {
  showAlert: (item: Omit<NotificationItem, "id">) => void;
  showSnackbar: (item: Omit<NotificationItem, "id">) => void;
  closeAlert: () => void;
  closeSnackbar: (id: number) => void;
};

const NotificationContext = createContext<NotificationContextValue | undefined>(
  undefined,
);

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [alert, setAlert] = useState<NotificationItem | null>(null);
  const [snackbars, setSnackbars] = useState<NotificationItem[]>([]);
  const alertTimeoutRef = useRef<number | null>(null);

  const clearAlertTimeout = useCallback(() => {
    if (alertTimeoutRef.current) {
      window.clearTimeout(alertTimeoutRef.current);
      alertTimeoutRef.current = null;
    }
  }, []);

  const closeAlert = useCallback(() => {
    clearAlertTimeout();
    setAlert(null);
  }, [clearAlertTimeout]);

  const showAlert = useCallback(
    (item: Omit<NotificationItem, "id">) => {
      clearAlertTimeout();
      setAlert({ ...item, id: Date.now() });
      alertTimeoutRef.current = window.setTimeout(() => {
        setAlert(null);
      }, item.duration ?? 5000);
    },
    [clearAlertTimeout],
  );

  const closeSnackbar = useCallback((id: number) => {
    setSnackbars((current) => current.filter((item) => item.id !== id));
  }, []);

  const showSnackbar = useCallback((item: Omit<NotificationItem, "id">) => {
    const notification = {
      ...item,
      id: Date.now() + Math.floor(Math.random() * 1000),
    };
    setSnackbars((current) => [...current, notification]);
    window.setTimeout(() => {
      setSnackbars((current) =>
        current.filter((entry) => entry.id !== notification.id),
      );
    }, item.duration ?? 3500);
  }, []);

  const value = useMemo<NotificationContextValue>(
    () => ({
      showAlert,
      showSnackbar,
      closeAlert,
      closeSnackbar,
    }),
    [showAlert, showSnackbar, closeAlert, closeSnackbar],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {alert ? (
        <Stack
          sx={{
            position: "fixed",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1700,
            width: { xs: "calc(100% - 24px)", sm: "auto" },
            pointerEvents: "none",
          }}
        >
          <Alert
            onClose={closeAlert}
            severity={
              alert.variant === "error"
                ? "error"
                : alert.variant === "success"
                  ? "success"
                  : "info"
            }
            variant="filled"
            sx={{
              pointerEvents: "auto",
              width: { xs: "100%", sm: 480 },
              borderRadius: 4,
              boxShadow: "0 20px 45px rgba(15, 23, 42, 0.18)",
              border: "1px solid rgba(255,255,255,0.24)",
            }}
          >
            {alert.title ? <AlertTitle>{alert.title}</AlertTitle> : null}
            {alert.message}
          </Alert>
        </Stack>
      ) : null}
      <Stack
        spacing={1.25}
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          zIndex: 1600,
          pointerEvents: "none",
        }}
      >
        {snackbars.map((item) => (
          <Alert
            key={item.id}
            severity={
              item.variant === "error"
                ? "error"
                : item.variant === "success"
                  ? "success"
                  : "info"
            }
            variant="outlined"
            action={
              <IconButton
                size="small"
                onClick={() => closeSnackbar(item.id)}
                sx={{ pointerEvents: "auto" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
            sx={{
              minWidth: 320,
              pointerEvents: "auto",
              boxShadow: "0 16px 34px rgba(15, 23, 42, 0.12)",
              borderRadius: 4,
            }}
          >
            {item.title ? (
              <Typography fontWeight={700}>{item.title}</Typography>
            ) : null}
            <Typography variant="body2">{item.message}</Typography>
          </Alert>
        ))}
      </Stack>
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }

  return context;
}
